import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowRight, Lock, CheckCircle2 } from "lucide-react";

const QV_LOGO =
  "https://customer-assets.emergentagent.com/job_athletic-edge-19/artifacts/7jyn9i1w_IMG_2453.PNG";

export default function SetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, email: "", requires_password: true, error: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) {
        setState({ loading: false, error: "Enlace inválido: falta el token." });
        return;
      }
      try {
        const { data } = await api.post("/auth/magic-link/verify", { token });
        if (!alive) return;
        setState({ loading: false, email: data.email, requires_password: data.requires_password, error: "" });
      } catch (err) {
        if (!alive) return;
        setState({ loading: false, error: formatErr(err) });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password });
      localStorage.setItem("qv_token", data.token);
      setUser(data.user);
      toast.success("Cuenta activada. Bienvenido.");
      navigate("/app/programas");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-5 py-12" data-testid="set-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <img src={QV_LOGO} alt="QV" className="h-12 w-12 object-contain" />
          <div>
            <div className="qv-display text-white text-xl">QUIQUE VARGAS</div>
            <div className="text-[10px] tracking-[0.32em] text-neutral-500 uppercase">Performance Coach</div>
          </div>
        </div>

        <div className="border border-white/10 bg-[#0a0a0a] p-8 sm:p-10">
          {state.loading && (
            <div className="text-neutral-400 text-sm text-center">Verificando enlace...</div>
          )}

          {!state.loading && state.error && (
            <div className="text-center space-y-4" data-testid="set-password-error">
              <div className="qv-overline text-red-500">Enlace inválido</div>
              <p className="text-neutral-300">{state.error}</p>
              <a href="/app/login" className="qv-btn-ghost inline-flex mt-4">Ir a Iniciar sesión</a>
            </div>
          )}

          {!state.loading && !state.error && (
            <>
              <div className="qv-overline">ACTIVA TU CUENTA</div>
              <h1 className="qv-display text-white text-3xl sm:text-4xl mt-3">
                {state.requires_password ? "Elige tu contraseña" : "Cambia tu contraseña"}
              </h1>
              <p className="mt-3 text-neutral-400 text-sm" data-testid="set-password-email">
                Cuenta: <span className="text-white font-semibold">{state.email}</span>
              </p>

              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="set-password-form">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block mb-2">
                    Nueva contraseña
                  </label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="qv-input pl-10"
                      placeholder="Mínimo 6 caracteres"
                      minLength={6}
                      required
                      data-testid="set-password-input"
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block mb-2">
                    Confirma contraseña
                  </label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                    <input
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="qv-input pl-10"
                      placeholder="Repite la contraseña"
                      minLength={6}
                      required
                      data-testid="set-password-confirm-input"
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="qv-btn-primary w-full justify-center mt-6 disabled:opacity-60"
                  data-testid="set-password-submit"
                >
                  {submitting ? "GUARDANDO..." : "ACTIVAR CUENTA"}
                  {!submitting && <ArrowRight size={18} />}
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-white/10 flex items-start gap-3 text-xs text-neutral-500">
                <CheckCircle2 size={14} color="var(--qv-red)" className="mt-0.5 shrink-0" />
                <span>Después de activar, entrarás directamente en tu área con los programas comprados.</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
